import { Component, ElementRef, OnInit } from '@angular/core';
import { Subject, take, takeUntil, tap } from "rxjs";
import { DialogService, DynamicDialogComponent, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { HostService } from "../../../services/hosts/host.service";
import { MessageService } from "primeng/api";
import { HostModel } from "../../../models/hosts/host.model";
import { AddHostComponent } from "../add-host/add-host.component";
import { TranslateService } from "@ngx-translate/core";
import { AddOrEditHostModel } from "../../../models/hosts/add-or-edit-host.model";

@Component({
  selector: 'app-edit-hosts',
  templateUrl: './edit-hosts.component.html',
  styleUrls: ['./edit-hosts.component.scss']
})
export class EditHostsComponent implements OnInit {

  //#region Fields
  public unsubscribe: Subject<void> = new Subject<void>();
  public errorModel: { [k: string]: any } = {};
  public spinner: boolean = false;
  public requiredFieldErrorMessage = "INFO.REQUIRED_FIELD";
  public venueId: string;
  public companyId: string;
  public hosts: Array<HostModel> = new Array<HostModel>();
  public clonedHosts: { [s: string]: HostModel; } = {};
  public hostsUpdated: boolean = false;
  //#endregion

  //#region Constructor
  constructor(private openFile: ElementRef,
              public dialogService: DialogService,
              public translate: TranslateService,
              private refDialogComponent: DynamicDialogComponent,
              private ref: DynamicDialogRef,
              private config: DynamicDialogConfig,
              public hostService: HostService,
              public messageService: MessageService) {
  }

  ngOnInit(): void {
    if (this.config.data != undefined && this.config.data.id && this.config.data.companyId) {
      this.venueId = this.config.data.id;
      this.companyId = this.config.data.companyId;
      this.loadHostsForVenue();
    }
  }

  //#endregion

  //#region Public Methods
  loadHostsForVenue() {
    this.spinner = true;
    this.hostService.listHostsByVenue(this.venueId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.spinner = false;
          this.hosts = response;
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  public updateHost(model: HostModel) {
    this.errorModel = {};

    if (!model.firstName || model.firstName == "") {
      this.errorModel['FirstName'] = this.requiredFieldErrorMessage;
    }
    if (!model.lastName || model.lastName == "") {
      this.errorModel['LastName'] = this.requiredFieldErrorMessage;
    }
    if (!model.phone || model.phone == "") {
      this.errorModel['Phone'] = this.requiredFieldErrorMessage;
    }

    if (Object.keys(this.errorModel).length === 0) {
      delete this.clonedHosts[model.id];

      this.spinner = true;

      const editModel = new AddOrEditHostModel();
      editModel.firstName = model.firstName;
      editModel.lastName = model.lastName;
      editModel.hostId = model.id;
      editModel.phone = model.phone.toString();

      this.hostService.updateHost(editModel)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (response) => {
            this.loadHostsForVenue();
            this.hostsUpdated = true;
          },
          error: errorResponse => {
            this.spinner = false;
            this.messageService.add({severity: 'error', summary: "System error", life: 2000});
          }
        });
    }
  }

  deactivateHost(hostId: string) {
    this.spinner = true;
    this.hostService.deactivateHost(hostId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response) => {
          this.loadHostsForVenue();
          this.hostsUpdated = true;
        },
        error: errorResponse => {
          this.spinner = false;
          this.messageService.add({severity: 'error', summary: "System error", life: 2000});
        }
      });
  }

  cancel(): void {
    this.ref.close(this.hostsUpdated);
  }

  onRowEditInit(host: HostModel) {
    this.clonedHosts[host.id] = {...host};
  }

  onRowEditSave(host: HostModel) {
    this.updateHost(host);
  }

  onRowEditCancel(host: HostModel, index: number) {
    this.hosts[index] = this.clonedHosts[host.id];
    delete this.clonedHosts[host.id];
  }

  openAddHostsPopup() {
    this.refDialogComponent.unbindDocumentKeydownListener();

    const ref = this.dialogService.open(AddHostComponent, {
      data: {
        id: this.venueId,
        companyId: this.companyId
      },
      header: this.translate.instant('HOST.ADD_HOSTS'),
      width: '650px'
    });

    ref.onClose.subscribe((item: any) => {
      if (item) {
        this.loadHostsForVenue();
        this.hostsUpdated = true;
      }
    });

    ref.onDestroy.pipe(
      take(1),
      tap(() => this.refDialogComponent.bindDocumentKeydownListener())).subscribe();
  }

  //#endregion
}
